import { TextField } from '@servicetitan/anvil2';
import { Controller, get, useFormContext } from 'react-hook-form';
import { useMaskito } from '@maskito/react';
import { maskitoWithPlaceholder } from '@maskito/kit';

interface PhoneInputProps {
    /**
     * Optional custom mask for phone numbers.
     * Default is US format: "(999) 999-9999"
     */
    mask?: string;
    /**
     * Field name for React Hook Form
     */
    name: string;
    required?: boolean;
    description?: string;
    /**
     * Label for the input
     */
    label?: string;
}

// Formats a phone number string to the format "(XXX) XXX-XXXX"
const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return value;
};

const maskitoOptions = {
    ...maskitoWithPlaceholder('(___) ___-____', false),
    mask: ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
};

/**
 * Phone input with mask that integrates with React Hook Form.
 * By default uses US phone format: (XXX) XXX-XXXX
 */
export const PhoneInput = ({ name, label, required, description }: PhoneInputProps) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();
    const maskedInputRef = useMaskito({ options: maskitoOptions });

    const error = get(errors, name);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => {
                const { ref, value, ...rest } = field;

                // If value is all numbers, format it as a phone number
                const formattedValue = /^\d+$/.test(value) ? formatPhoneNumber(value) : value;

                return (
                    <TextField
                        ref={element => {
                            maskedInputRef(element);
                            ref(element);
                        }}
                        label={label}
                        required={required}
                        description={description}
                        value={formattedValue}
                        error={error != null ? error.message : undefined}
                        inputMode="numeric"
                        {...rest}
                        type="tel"
                    />
                );
            }}
        />
    );
};
